import type { CampaignDraftPostItem } from '@/entities/campaign';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import {
  type PostCsvPreviewState,
  buildPostBulkPastePreview,
  warnUnsupportedCampaignPlatforms,
  withFallbackPostRow,
} from '../../../../hooks';

type UseCampaignPostBulkPasteArgs = {
  campaignItemSlotLimit: number;
  draftPostItems: CampaignDraftPostItem[];
  remainingSlots: number;
  setPostItems: Dispatch<SetStateAction<CampaignDraftPostItem[]>>;
};

export function useCampaignPostBulkPaste({
  campaignItemSlotLimit,
  draftPostItems,
  remainingSlots,
  setPostItems,
}: UseCampaignPostBulkPasteArgs) {
  const getPostBulkPastePreview = useCallback(
    (text: string) => {
      if (!text.trim()) return undefined;

      return buildPostBulkPastePreview({
        text,
        existingRows: draftPostItems,
        availableSlots: remainingSlots,
        itemLimit: campaignItemSlotLimit,
      });
    },
    [campaignItemSlotLimit, draftPostItems, remainingSlots],
  );

  const applyPostBulkPaste = useCallback(
    (preview: PostCsvPreviewState) => {
      const unsupportedPlatformWarning = warnUnsupportedCampaignPlatforms(
        preview.unsupportedPlatformCount,
      );
      if (preview.summary.added === 0) return;
      setPostItems(withFallbackPostRow(preview.rows));
      if (unsupportedPlatformWarning) {
        toast.warning(unsupportedPlatformWarning);
      }
    },
    [setPostItems],
  );

  return {
    applyPostBulkPaste,
    getPostBulkPastePreview,
  };
}
